import React, { useState, useEffect } from 'react';
import './Opportunities.css';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CHARACTER_KEYS = {
  'lady macbeth': ['lady macbeth'],
  'falstaff': ['falstaff'],
  'miranda': ['miranda'],
  'iago': ['iago'],
  'sir andrew aguecheek': ['sir andrew', 'sir andrew aguecheek'],
  'sir andrew': ['sir andrew', 'sir andrew aguecheek'],
};

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [activeTab, setActiveTab] = useState('reply'); // reply | promotions | prospects
  const [charAvatars, setCharAvatars] = useState({});
  const [toast, setToast] = useState(null); // Auto-dismissing notifications

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get admin token from localStorage
      const adminToken = localStorage.getItem('admin_jwt');
      
      if (!adminToken) {
        setShowLogin(true);
        setLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('admin_jwt');
        setShowLogin(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        // Any auth-like error should trigger login, not generic error
        if (response.status >= 400 && response.status < 500) {
          localStorage.removeItem('admin_jwt');
          setShowLogin(true);
          setLoading(false);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setOpportunities(data.opportunities || []);
      setMetadata({
        lastScan: data.last_scan_mt,
        nextScan: data.next_scan_in_minutes,
        currentTime: data.current_time_mt
      });
      
      // Also fetch promotions (full scene candidates)
      try {
        const promoResponse = await fetch(`${BACKEND_URL}/api/admin/promotions`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (promoResponse.ok) {
          const promoData = await promoResponse.json();
          setPromotions(promoData.promotions || []);
        }
      } catch {
        // Promotions are optional, don't fail if unavailable
      }
      
      // Fetch new account prospects
      try {
        const prospectResponse = await fetch(`${BACKEND_URL}/api/admin/opportunities/prospects`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (prospectResponse.ok) {
          const prospectData = await prospectResponse.json();
          setProspects((prospectData.prospects || []).sort((a, b) => {
            const order = { Gold: 0, Silver: 1, Bronze: 2 };
            return (order[a.rating] ?? 3) - (order[b.rating] ?? 3) || (b.score || 0) - (a.score || 0);
          }));
        }
      } catch (err) {
        console.warn('Prospector endpoint unavailable:', err);
      }
      
      setLoading(false);
      setCountdown(60);
      
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Load character avatars for our troupe (used on Replies tab)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/ui/chat/avatars`);
        if (r.ok) {
          const data = await r.json();
          setCharAvatars(data || {});
        }
      } catch {
        // Non-fatal for Opportunities if avatars endpoint fails
      }
    })();
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchOpportunities();
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        setLoginError('Invalid password');
        return;
      }

      const data = await response.json();
      localStorage.setItem('admin_jwt', data.token);
      setShowLogin(false);
      setPassword('');
      fetchOpportunities();
      
    } catch (err) {
      setLoginError('Login failed: ' + err.message);
    }
  };

  const copyReplyCommand = (character, postUrl, oppId) => {
    // Map character names to Discord command shortcuts
    const charMap = {
      'Lady Macbeth': 'LM',
      'Falstaff': 'F',
      'Miranda': 'M',
      'Iago': 'I',
      'Sir Andrew Aguecheek': 'Andrew',
      'Sir Andrew': 'Andrew'
    };
    
    const charToken = charMap[character] || character;
    const command = `!reply ${charToken} ${postUrl}`;
    navigator.clipboard.writeText(command).then(() => {
      alert(`Copied to clipboard:\n${command}\n\nPaste in Discord admin channel!`);
      // Dismiss-on-copy: once we've generated the command, treat this opportunity as handled.
      if (oppId) {
        skipOpportunity(oppId);
      }
    });
  };

  const skipOpportunity = async (oppId) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/${oppId}/skip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        fetchOpportunities();
      }
    } catch (err) {
      console.error('Error skipping:', err);
    }
  };

  const approveProspect = async (handle, tier = 2) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/prospects/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handle, tier })
      });
      if (response.ok) {
        fetchOpportunities();
      }
    } catch (err) {
      alert(`Failed to approve ${handle}: ${err.message}`);
    }
  };

  const dismissProspect = async (handle) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/prospects/dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handle })
      });
      if (response.ok) {
        fetchOpportunities();
      }
    } catch (err) {
      alert(`Failed to dismiss ${handle}: ${err.message}`);
    }
  };

  const showToast = (message, duration = 3000) => {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  };

  const postReplyToDiscord = async (character, postUrl, oppId) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/post-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ character, post_url: postUrl })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      // Mark as skipped to remove from list
      await skipOpportunity(oppId);
      
      showToast(`✅ Posted to Discord: ${result.command}`);
      
    } catch (err) {
      showToast(`❌ Failed: ${err.message}`, 5000);
    }
  };

  if (showLogin) {
    return (
      <div className="opportunities-container">
        <h1>🔐 Admin Login</h1>
        <div className="login-container">
          <form onSubmit={handleLogin} className="login-form">
            <p>Enter admin password to access reply opportunities:</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="password-input"
            />
            <button type="submit" className="btn-primary">
              Login
            </button>
            {loginError && (
              <div className="login-error">{loginError}</div>
            )}
          </form>
        </div>
      </div>
    );
  }

  if (loading && opportunities.length === 0) {
    return (
      <div className="opportunities-container">
        <h1>🎯 Reply Opportunities</h1>
        <div className="loading">Loading opportunities...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="opportunities-container">
        <h1>🎯 Reply Opportunities</h1>
        <div className="error">
          <h2>⚠️ {error}</h2>
          <button onClick={fetchOpportunities} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunities-container">
      <h1>🎯 Opportunities Desk</h1>

      <div className="tab-bar">
        <button
          className={`tab-button ${activeTab === 'reply' ? 'active' : ''}`}
          onClick={() => setActiveTab('reply')}
        >
          Replies
        </button>
        <button
          className={`tab-button ${activeTab === 'promotions' ? 'active' : ''}`}
          onClick={() => setActiveTab('promotions')}
        >
          Promotions
        </button>
        <button
          className={`tab-button ${activeTab === 'prospects' ? 'active' : ''}`}
          onClick={() => setActiveTab('prospects')}
        >
          New Accounts
        </button>
      </div>
      
      {metadata && activeTab === 'reply' && (
        <div className="data-freshness">
          📡 Last scan: <strong>{metadata.lastScan || 'Never'}</strong> MT | 
          Next scan: <strong>{(() => {
            if (!metadata.lastScan) return '~30 min';
            try {
              // Safari-safe date parsing: convert "2025-11-16 14:41:35" to ISO format
              const isoDate = metadata.lastScan.replace(' ', 'T') + '-07:00';
              const lastScan = new Date(isoDate);
              
              if (isNaN(lastScan.getTime())) {
                return '~30 min';  // Fallback if parse fails
              }
              
              const now = new Date();
              const minutesSince = Math.floor((now - lastScan) / 60000);
              const minutesUntil = Math.max(0, 30 - minutesSince);
              return minutesUntil === 0 ? 'scanning...' : minutesUntil + ' min';
            } catch {
              return '~30 min';
            }
          })()}</strong>
          <div style={{fontSize: '11px', opacity: 0.7, marginTop: '5px'}}>
            This page auto-refreshes every 60s (in {countdown}s) to fetch new opportunities from database
          </div>
        </div>
      )}

      {activeTab === 'reply' && (
        <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{opportunities.length}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {opportunities.filter(o => o.urgency === 'high').length}
          </div>
          <div className="stat-label">High Urgency (&lt;30 min)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {opportunities.filter(o => o.age_minutes < 60).length}
          </div>
          <div className="stat-label">Fresh (&lt;1 hour)</div>
        </div>
      </div>

      {opportunities.length === 0 ? (
        <div className="empty-state">
          <h2>No opportunities right now</h2>
          <p>Scanner will check again in ~{metadata?.nextScan || 30} minutes</p>
        </div>
      ) : (
        <div className="opportunities-list">
          {opportunities.map(opp => (
            <div key={opp.id} className={`opportunity ${opp.urgency}`}>
              <div className="opp-header">
                <div className="score">⭐ {Math.round(opp.visibility_score)}/100</div>
                <div className="opp-meta">
                    <div className="author">
                      {opp.avatar_url && (
                        <img
                          src={opp.avatar_url}
                          alt={opp.author_display || opp.author}
                          className="opp-avatar"
                        />
                      )}
                      <span>
                        {opp.author_display ? `${opp.author_display} ` : ''}@{opp.author}
                      </span>
                    </div>
                  <div className="age">
                    {opp.followers.toLocaleString()} followers · 
                    Posted {opp.age_minutes}m ago
                  </div>
                </div>
              </div>

              <div className="post-text">"{opp.text}"</div>

              <div className="character-match">
                <span className="character-name">
                  {(() => {
                    const raw = String(opp.character || '');
                    const key = raw.toLowerCase();
                    const mapping = CHARACTER_KEYS[key];
                    let avatarSrc = null;
                    if (mapping && charAvatars) {
                      for (const m of mapping) {
                        if (charAvatars[m]) {
                          avatarSrc = charAvatars[m];
                          break;
                        }
                      }
                    }
                    return avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={raw}
                        className="char-avatar"
                      />
                    ) : null;
                  })()}
                  🎭 {opp.character}
                </span>
                <span className="fit-score">
                  ({Math.round(opp.character_fit * 100)}% fit)
                </span>
              </div>

                  {opp.cooldown_flag && (
                    <div className="cooldown-flag">
                      ⚠ Already replied twice within 48h — consider holding.
                    </div>
                  )}

              <div className="actions">
                <button 
                  className="btn-link"
                  onClick={() => window.open(opp.post_url, '_blank')}
                >
                  View Post
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => skipOpportunity(opp.id)}
                >
                  Skip
                </button>
              </div>
              
              {!opp.cooldown_flag && (
                <div className="character-buttons">
                  <div style={{fontSize: '11px', opacity: 0.7, marginBottom: '6px'}}>
                    Post reply as:
                  </div>
                  <div className="char-button-row">
                    <button 
                      className="btn-char"
                      onClick={() => postReplyToDiscord('F', opp.post_url, opp.id)}
                      title="Falstaff"
                    >
                      F
                    </button>
                    <button 
                      className="btn-char"
                      onClick={() => postReplyToDiscord('LM', opp.post_url, opp.id)}
                      title="Lady Macbeth"
                    >
                      LM
                    </button>
                    <button 
                      className="btn-char"
                      onClick={() => postReplyToDiscord('M', opp.post_url, opp.id)}
                      title="Miranda"
                    >
                      M
                    </button>
                    <button 
                      className="btn-char"
                      onClick={() => postReplyToDiscord('I', opp.post_url, opp.id)}
                      title="Iago"
                    >
                      I
                    </button>
                    <button 
                      className="btn-char"
                      onClick={() => postReplyToDiscord('Andrew', opp.post_url, opp.id)}
                      title="Sir Andrew Aguecheek"
                    >
                      Andrew
                    </button>
                  </div>
                </div>
              )}
              
              {opp.cooldown_flag && (
                <div className="cooldown-notice">
                  ⚠ Cooldown: Already replied twice within 48h
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {activeTab === 'promotions' && (
          <div className="opportunities-list">
          {promotions && promotions.length > 0 ? (
            promotions.map(promo => (
              <div key={promo.id} className="opportunity promo">
                <div className="opp-header">
                  <div className="score">⭐ {Math.round(promo.promo_score)}/100</div>
                  <div className="opp-meta">
                    <div className="author">
                      {promo.avatar_url && (
                        <img
                          src={promo.avatar_url}
                          alt={promo.author_display || promo.author}
                          className="opp-avatar"
                        />
                      )}
                      <span>
                        {promo.author_display ? `${promo.author_display} ` : ''}@{promo.author}
                      </span>
                    </div>
                    <div className="age">
                      {promo.followers.toLocaleString()} followers · 
                      Posted {promo.age_hours}h ago
                    </div>
                  </div>
                </div>

                <div className="post-text">"{promo.text}"</div>

                <div className="character-match">
                  <span className="character-name">🎭 Full Troupe (5 characters)</span>
                  <span className="fit-score">
                    Quality: {Math.round((promo.content_quality || 0) * 100)}% | 
                    Fit: {Math.round((promo.topic_alignment || 0) * 100)}%
                  </span>
                </div>

                <div className="actions">
                  <button 
                    className="btn-link"
                    onClick={() => window.open(promo.post_url, '_blank')}
                  >
                    View Post
                  </button>
                  <button 
                    className="btn-primary"
                    style={{background: '#9c27b0'}}
                    onClick={() => {
                      const command = `!promote ${promo.post_url}`;
                      navigator.clipboard.writeText(command).then(() => {
                        alert(`Copied to clipboard:\n${command}\n\nPaste in Discord admin channel!\n\nThis will create a full scene with all 5 characters.`);
                      });
                    }}
                  >
                    Create Scene (!promote)
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        const adminToken = localStorage.getItem('admin_jwt');
                        const response = await fetch(`${BACKEND_URL}/api/admin/promotions/${promo.id}/skip`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${adminToken}` }
                        });
                        if (response.ok) {
                          fetchOpportunities();
                        }
                      } catch (err) {
                        console.error('Error skipping:', err);
                      }
                    }}
                  >
                    Skip
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <h2>No promotion candidates right now</h2>
              <p>The crawler will provide fresh !promote targets after the next scan.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'prospects' && (
        <div className="opportunities-list">
          {prospects.length === 0 ? (
            <div className="empty-state">
              <h2>No pending prospects</h2>
              <p>Run the prospector script to refresh recommendations.</p>
            </div>
          ) : (
            prospects.map((prospect, idx) => {
              const followers = typeof prospect.followers === 'number' ? prospect.followers : 0;
              const followersLabel = followers > 0
                ? `${followers.toLocaleString()} followers`
                : 'New or low-visibility account';
              const reasonText = (prospect.reason || '').replace(/^\s*0 followers ·\s*/i, '');
              return (
              <div key={`${prospect.handle}-${idx}`} className="prospect-card">
                <div className="prospect-header">
                  <div>
                    <div className="author">
                      {prospect.avatar_url && (
                        <img
                          src={prospect.avatar_url}
                          alt={prospect.author_display || prospect.handle}
                          className="opp-avatar"
                        />
                      )}
                      <span>
                        {prospect.author_display ? `${prospect.author_display} ` : ''}@{prospect.handle}
                      </span>
                    </div>
                    <div className="age">
                      {followersLabel}{reasonText ? ` · ${reasonText}` : ''}
                    </div>
                  </div>
                  <div className={`rating-badge rating-${(prospect.rating || 'Bronze').toLowerCase()}`}>
                    {prospect.rating || 'Bronze'}
                  </div>
                </div>
                <div className="post-text">"{prospect.last_reply_text}"</div>
                <div className="character-match">
                  <span className="character-name">
                    📈 Alignment {Math.round((prospect.alignment_score || 0) * 100)}%
                  </span>
                  <span className="fit-score">
                    Archetype: {prospect.best_archetype}
                  </span>
                </div>
                <div className="actions">
                  <button 
                    className="btn-link"
                    onClick={() => {
                      const url = prospect.reply_url || prospect.source_post_url;
                      if (url) window.open(url, '_blank');
                    }}
                    disabled={!prospect.reply_url && !prospect.source_post_url}
                  >
                    View Reply
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={() => approveProspect(prospect.handle, 2)}
                  >
                    Approve Target
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={() => dismissProspect(prospect.handle)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )})
          )}
          </div>
      )}
      
      {toast && (
        <div className="toast-notification">
          {toast}
          </div>
      )}
    </div>
  );
};

export default Opportunities;

